import { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { LoadingButton } from "@mui/lab";
import { BaseTable } from "../../BaseTable/BaseTable";
import MuiConfirmationDialog from "../../@material-extend/MuiConfirmationDialog";
import { useAuth } from "src/hooks/useAuth.hook";
import { useAccess } from "src/hooks/useAccess.hook";
import { useList, useMutate } from "src/hooks/useFetch.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { FEATURE } from "src/util/enums";
import { fDateTime } from "src/utils/formatTime";
import {
  getTeamUrl,
  fleetChargerAccessUrl,
  fleetChargerAccessIdUrl,
} from "src/react-query/endPoints";
import {
  ACCESS_SCOPES,
  ACCESS_SCOPE_OPTIONS,
  toList,
  statusChipColor,
} from "../fleet.constants";

const getAccessId = (row) => row?.access_id ?? row?.accessId ?? row?.id;

const ChargerAccessTab = ({ operatorId }) => {
  const { teamId } = useSelectedTeam();
  const { user } = useAuth();
  const isScopedOperator = user?.company?.fleetOperatorScope != null;
  // `user` loads asynchronously (Redux, hydrated after the profile fetch), so
  // gate CPO-only fetches on a positive "profile loaded AND not scoped" signal —
  // otherwise the query fires during the loading window before scope is known.
  const isCpoAdmin = user?.company != null && !isScopedOperator;
  const hasCreateAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "C");
  const hasDeleteAccess = useAccess(FEATURE.FLEET_MANAGEMENT, "D");

  const {
    data: accessData,
    isFetching,
  } = useList("fleetChargerAccess", undefined, operatorId);
  // Teams/chargers populate the grant dialog, which is CPO-only. Scoped operators
  // never see that dialog, and these are general CMS endpoints their FLEET_MANAGEMENT-only
  // role can't read — so skip the fetches (avoids a spurious 403 error toast).
  const { data: teamsData } = useList("teams", isCpoAdmin);
  const { data: chargersData } = useList("chargers", isCpoAdmin);

  const accessRecords = useMemo(() => toList(accessData), [accessData]);
  const teams = useMemo(() => toList(teamsData), [teamsData]);
  const chargers = useMemo(
    () => chargersData?.chargers ?? (Array.isArray(chargersData) ? chargersData : []),
    [chargersData]
  );

  const { mutate, isLoading } = useMutate("fleetChargerAccess", {
    id: operatorId,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [scope, setScope] = useState(ACCESS_SCOPES.COMPANY);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [selectedCharger, setSelectedCharger] = useState(null);
  const [revokingAccess, setRevokingAccess] = useState(null);

  const teamNameById = useMemo(
    () =>
      teams.reduce((acc, team) => {
        const tid = team?.teamId ?? team?.companyId ?? team?.id;
        acc[tid] = team?.teamName ?? team?.companyName ?? team?.name ?? `#${tid}`;
        return acc;
      }, {}),
    [teams]
  );

  const resetDialog = () => {
    setScope(ACCESS_SCOPES.COMPANY);
    setSelectedTeamId("");
    setSelectedCharger(null);
  };

  const canSubmit =
    scope === ACCESS_SCOPES.COMPANY ||
    (scope === ACCESS_SCOPES.TEAM && selectedTeamId) ||
    (scope === ACCESS_SCOPES.CHARGER && selectedCharger);

  const onGrantAccess = () => {
    const body = { access_scope: scope };
    if (scope === ACCESS_SCOPES.TEAM) body.team_id = selectedTeamId;
    if (scope === ACCESS_SCOPES.CHARGER)
      body.charger_id =
        selectedCharger?.chargepoint_id ??
        selectedCharger?.charge_box_id ??
        selectedCharger;
    mutate(
      {
        url: getTeamUrl(teamId, fleetChargerAccessUrl(operatorId)),
        method: "POST",
        body,
      },
      {
        onSuccess: () => {
          setDialogOpen(false);
          resetDialog();
        },
      }
    );
  };

  const onRevokeAccess = () => {
    mutate(
      {
        url: getTeamUrl(
          teamId,
          fleetChargerAccessIdUrl(operatorId, getAccessId(revokingAccess))
        ),
        method: "DELETE",
      },
      {
        onSuccess: () => {
          setRevokingAccess(null);
        },
      }
    );
  };

  const columns = useMemo(
    () => [
      {
        field: "access_scope",
        headerName: "Scope",
        flex: 0.7,
        renderCell: ({ value }) => (
          <Chip size="small" variant="outlined" label={value ?? "-"} />
        ),
      },
      {
        field: "team_id",
        headerName: "Team",
        flex: 1,
        renderCell: ({ row, value }) =>
          row?.access_scope === ACCESS_SCOPES.TEAM
            ? teamNameById[value] ?? `#${value}`
            : "-",
      },
      {
        field: "charger_id",
        headerName: "Charger",
        flex: 1,
        renderCell: ({ row, value }) =>
          row?.access_scope === ACCESS_SCOPES.CHARGER ? value ?? "-" : "-",
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.6,
        renderCell: ({ value }) => (
          <Chip size="small" label={value ?? "-"} color={statusChipColor(value)} />
        ),
      },
      {
        field: "created_at",
        headerName: "Granted On",
        flex: 0.9,
        renderCell: ({ value }) => fDateTime(value) || "-",
      },
      {
        field: "Actions",
        headerName: "Actions",
        width: 90,
        sortable: false,
        renderCell: ({ row }) => (
          <>
            {hasDeleteAccess && !isScopedOperator && (
              <Tooltip title="Revoke Access">
                <IconButton size="small" onClick={() => setRevokingAccess(row)}>
                  <Icon icon="eva:trash-2-outline" />
                </IconButton>
              </Tooltip>
            )}
          </>
        ),
      },
    ],
    [teamNameById, hasDeleteAccess, isScopedOperator]
  );

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Charger Access</Typography>
        {hasCreateAccess && !isScopedOperator && (
          <Button
            variant="contained"
            startIcon={<Icon icon="eva:plus-fill" />}
            onClick={() => setDialogOpen(true)}
          >
            Grant Access
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Defines which chargers this fleet operator&apos;s drivers may use —
        company-wide, per team, or per charger.
      </Typography>
      <BaseTable
        rows={accessRecords}
        columns={columns}
        loading={isFetching}
        getRowId={(row) => getAccessId(row)}
        pageSize={10}
      />
      {!isFetching && !accessRecords.length && (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ py: 2 }}
        >
          No charger access granted yet.
        </Typography>
      )}

      {/* Grant access dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Grant Charger Access</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <FormControl fullWidth size="small">
              <InputLabel id="access-scope">Access Scope</InputLabel>
              <Select
                labelId="access-scope"
                label="Access Scope"
                value={scope}
                onChange={(e) => {
                  setScope(e.target.value);
                  setSelectedTeamId("");
                  setSelectedCharger(null);
                }}
              >
                {ACCESS_SCOPE_OPTIONS.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {scope === ACCESS_SCOPES.TEAM && (
              <FormControl fullWidth size="small" required>
                <InputLabel id="access-team">Team</InputLabel>
                <Select
                  labelId="access-team"
                  label="Team"
                  value={selectedTeamId}
                  onChange={(e) => setSelectedTeamId(e.target.value)}
                >
                  {teams.map((team) => {
                    const tid = team?.teamId ?? team?.companyId ?? team?.id;
                    return (
                      <MenuItem key={tid} value={tid}>
                        {teamNameById[tid]}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}
            {scope === ACCESS_SCOPES.CHARGER && (
              <Autocomplete
                options={chargers ?? []}
                size="small"
                value={selectedCharger}
                onChange={(event, newValue) => setSelectedCharger(newValue)}
                getOptionLabel={(option) =>
                  option?.chargepoint_id ?? option?.charge_box_id ?? String(option)
                }
                renderInput={(params) => (
                  <TextField {...params} label="Charger" placeholder="Select Charger" />
                )}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disabled={!canSubmit}
            loading={isLoading}
            onClick={onGrantAccess}
          >
            Grant
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Revoke confirm */}
      <MuiConfirmationDialog
        open={Boolean(revokingAccess)}
        onClose={() => setRevokingAccess(null)}
        title="Revoke Charger Access"
        severity="warning"
        contentText={`Revoke ${revokingAccess?.access_scope ?? ""} access${
          revokingAccess?.charger_id ? ` to charger "${revokingAccess.charger_id}"` : ""
        }?`}
        closeText="Cancel"
        okText="Revoke"
        onOk={onRevokeAccess}
        isLoading={isLoading}
      />
    </>
  );
};

export default ChargerAccessTab;
